import React, { useEffect, useRef } from 'react';
import { Icon } from '@arc-ui/components';
import { ICON_CLOSE, ICON_SEARCH } from '../../atoms/SVG';
import { SearchOverlayProps } from '../../../types/search';

interface SearchOverlayInternalProps extends SearchOverlayProps {
  value: string;
  focused: boolean;
  onValueChange: (value: string) => void;
  onFocusChange: (focused: boolean) => void;
  onSubmit: () => void;
}

const SearchOverlay: React.FC<SearchOverlayInternalProps> = ({
  isOpen,
  onClose,
  overlayTop,
  isMobile,
  value,
  focused,
  onValueChange,
  onFocusChange,
  onSubmit,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const overlayShade = (() => {
    if (focused && value.trim()) return 'eeb-SearchOverlay-dim--dark';
    if (focused) return 'eeb-SearchOverlay-dim--light';
    return '';
  })();

  if (!isOpen) return null;

  return (
    <dialog
      className="eeb-SearchOverlay"
      open
      aria-modal="true"
      aria-label="Search overlay"
      style={
        {
          '--eeb-overlay-top': `${overlayTop}px`,
          position: 'fixed',
          inset: 0,
          margin: 0,
          padding: 0,
          border: 0,
          background: 'transparent',
          overflow: 'visible',
          zIndex: 9999,
        } as React.CSSProperties
      }
    >
      <div
        className={`eeb-SearchOverlay-dim ${overlayShade}`}
        style={{ top: overlayTop }}
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="eeb-SearchOverlay-bar" style={{ top: overlayTop }}>
        <div className="eeb-SearchOverlay-inner">
          <button
            aria-label="Close search"
            className="eeb-SearchOverlay-btn eeb-SearchOverlay-btn--outside"
            onClick={onClose}
          >
            <Icon icon={ICON_CLOSE} size={isMobile ? 24 : 32} label="Close" />
          </button>

          <div className="eeb-SearchOverlay-inputWrap">
            <input
              ref={inputRef}
              id="eeb-header-search"
              placeholder="Search"
              type="text"
              value={value}
              autoComplete="off"
              maxLength={100}
              onChange={(e) => onValueChange(e.target.value)}
              onFocus={() => onFocusChange(true)}
              onBlur={() => onFocusChange(false)}
              onKeyDown={(e) => e.key === 'Enter' && value.trim() && onSubmit()}
              aria-label="Search"
              className="eeb-SearchOverlay-input"
            />
            <button
              aria-label="Submit search"
              className="eeb-SearchOverlay-btn eeb-SearchOverlay-btn--right"
              onClick={() => value.trim() && onSubmit()}
              disabled={!value.trim()}
            >
              <Icon
                icon={ICON_SEARCH}
                size={isMobile ? 24 : 32}
                label="Search"
              />
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
};

export default SearchOverlay;
